import { Plus, Trash2 } from 'lucide-react';
import { useId } from 'react';
import type { BgRole, SpecialNeed } from '@tragfaehigkeit/core';
import type { ResourcesBgMemberDraft } from '../../lib/store/wizard';
import { de } from '../../lib/i18n/de';

interface BgCompositionEditorProps {
  members: readonly ResourcesBgMemberDraft[];
  onAdd: (member: Omit<ResourcesBgMemberDraft, 'id'>) => void;
  onUpdate: (memberId: string, patch: Partial<Omit<ResourcesBgMemberDraft, 'id'>>) => void;
  onRemove: (memberId: string) => void;
}

/**
 * Editor für die Bedarfsgemeinschaft (BG).
 *
 * Editorial-Liste mit großen Modul-artigen Karten pro Person. Antragsteller
 * ist immer Position 1 und kann nicht entfernt werden (Schema-Constraint).
 * Maximal ein Partner. Kinder beliebig viele.
 */
export function BgCompositionEditor({
  members,
  onAdd,
  onUpdate,
  onRemove,
}: BgCompositionEditorProps) {
  const t = de.steps.resources.subSteps.bg;
  const hasPartner = members.some((m) => m.role === 'partner');

  return (
    <fieldset className="space-y-0">
      <legend className="font-sans text-xs font-medium uppercase tracking-widest text-stone-500">
        {t.membersLabel}
      </legend>

      <ul className="mt-4 divide-y divide-stone-200 border-y border-stone-200">
        {members.map((m, i) => (
          <li key={m.id} className="py-6">
            <BgMemberRow
              index={i}
              member={m}
              onUpdate={(patch) => {
                onUpdate(m.id, patch);
              }}
              onRemove={() => {
                onRemove(m.id);
              }}
              canRemove={m.role !== 'antragsteller'}
            />
          </li>
        ))}
      </ul>

      <div className="mt-5 flex flex-wrap gap-3">
        {!hasPartner && (
          <button
            type="button"
            onClick={() => {
              onAdd({ role: 'partner', age: 35, specialNeeds: [] });
            }}
            className="inline-flex items-center gap-2 rounded-sm border border-stone-300 bg-white px-4 py-2 font-sans text-xs font-medium uppercase tracking-widest text-stone-700 transition-colors hover:bg-stone-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-stone-900 focus-visible:ring-offset-2"
          >
            <Plus size={14} aria-hidden="true" />
            {t.rolePartner}
          </button>
        )}
        <button
          type="button"
          onClick={() => {
            onAdd({ role: 'kind', age: 5, specialNeeds: [] });
          }}
          className="inline-flex items-center gap-2 rounded-sm border border-stone-300 bg-white px-4 py-2 font-sans text-xs font-medium uppercase tracking-widest text-stone-700 transition-colors hover:bg-stone-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-stone-900 focus-visible:ring-offset-2"
        >
          <Plus size={14} aria-hidden="true" />
          {t.roleKind}
        </button>
      </div>
    </fieldset>
  );
}

interface BgMemberRowProps {
  index: number;
  member: ResourcesBgMemberDraft;
  onUpdate: (patch: Partial<Omit<ResourcesBgMemberDraft, 'id'>>) => void;
  onRemove: () => void;
  canRemove: boolean;
}

function BgMemberRow({ index, member, onUpdate, onRemove, canRemove }: BgMemberRowProps) {
  const t = de.steps.resources.subSteps.bg;
  const ageId = useId();
  const roleLabel = roleToLabel(member.role);

  const togglesAvailable = member.role !== 'kind';

  const togglePregnancy = (next: boolean) => {
    onUpdate({
      specialNeeds: toggleNeed(member.specialNeeds, 'pregnancy', next),
    });
  };
  const toggleSingleParent = (next: boolean) => {
    onUpdate({
      specialNeeds: toggleNeed(member.specialNeeds, 'single_parent', next),
    });
  };

  return (
    <div className="flex flex-col gap-4 md:flex-row md:items-start md:gap-8">
      <div className="flex items-baseline gap-4 md:w-24">
        <span
          aria-hidden="true"
          className="font-serif text-3xl font-black tabular-nums leading-none text-stone-200"
        >
          {String(index + 1).padStart(2, '0')}
        </span>
      </div>

      <div className="flex-1 space-y-3">
        <p className="font-serif text-base font-bold uppercase tracking-tight text-stone-900">
          {roleLabel}
        </p>

        <div className="flex flex-wrap items-end gap-x-6 gap-y-3">
          <label htmlFor={ageId} className="block">
            <span className="font-sans text-xs font-medium uppercase tracking-widest text-stone-500">
              {t.memberAgeLabel}
            </span>
            <input
              id={ageId}
              type="number"
              inputMode="numeric"
              min={0}
              max={120}
              value={member.age}
              onChange={(e) => {
                const next = Number(e.target.value);
                if (!Number.isNaN(next) && next >= 0 && next <= 120) {
                  onUpdate({ age: next });
                }
              }}
              className="mt-1 w-24 border-b border-stone-300 bg-transparent py-1 font-serif text-2xl font-black tabular-nums tracking-tight text-stone-900 outline-none focus:border-stone-900"
            />
          </label>

          {togglesAvailable && (
            <div className="flex flex-col gap-2">
              <label className="inline-flex items-center gap-2 font-sans text-xs font-medium tracking-wide text-stone-700">
                <input
                  type="checkbox"
                  checked={member.specialNeeds.includes('pregnancy')}
                  onChange={(e) => {
                    togglePregnancy(e.target.checked);
                  }}
                  className="h-4 w-4 rounded-sm border-stone-300 text-stone-900 focus:ring-stone-900"
                />
                {t.memberPregnantLabel}
              </label>
              <label className="inline-flex items-center gap-2 font-sans text-xs font-medium tracking-wide text-stone-700">
                <input
                  type="checkbox"
                  checked={member.specialNeeds.includes('single_parent')}
                  onChange={(e) => {
                    toggleSingleParent(e.target.checked);
                  }}
                  className="h-4 w-4 rounded-sm border-stone-300 text-stone-900 focus:ring-stone-900"
                />
                {t.memberSingleParentLabel}
              </label>
            </div>
          )}
        </div>
      </div>

      {canRemove && (
        <button
          type="button"
          onClick={onRemove}
          aria-label={`${roleLabel} ${t.removeMember}`}
          className="inline-flex items-center gap-1.5 self-start rounded-sm border border-transparent px-3 py-2 font-sans text-xs font-medium uppercase tracking-widest text-stone-500 transition-colors hover:border-stone-200 hover:text-rose-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-stone-900 focus-visible:ring-offset-2"
        >
          <Trash2 size={14} aria-hidden="true" />
          {t.removeMember}
        </button>
      )}
    </div>
  );
}

function roleToLabel(role: BgRole): string {
  const t = de.steps.resources.subSteps.bg;
  switch (role) {
    case 'antragsteller':
      return t.roleAntragsteller;
    case 'partner':
      return t.rolePartner;
    case 'kind':
      return t.roleKind;
  }
}

function toggleNeed(
  needs: readonly SpecialNeed[],
  needle: SpecialNeed,
  add: boolean,
): readonly SpecialNeed[] {
  if (add) {
    if (needs.includes(needle)) return needs;
    return [...needs, needle];
  }
  return needs.filter((n) => n !== needle);
}
